import { ALL_LOCALES, FALLBACK_LOCALE, isLocale, type Locale } from './locales';

/**
 * The visitor's DELIBERATE language pick, written only by the language
 * selector. Deliberately not i18next's own `i18nextLng`: LanguageDetector
 * caches into that key on detection as well as on changeLanguage, which would
 * make a shop default indistinguishable from a real choice — and a shop that
 * later changed its default would never reach visitors it had already served
 * the old one to.
 */
export const CHOICE_KEY = 'f2m_locale';

/** Pre-paint cache of the shop's default, mirroring theme/themeCache.ts. */
export const SHOP_DEFAULT_KEY = 'f2m_shop_default_locale';

/**
 * Cache of the shop's offered locale set (accounts.enabled_locales /
 * users.enabled_locales), alongside SHOP_DEFAULT_KEY. Without this, the
 * offered set is only known while a live /accounts/info or /users/me query
 * has data for the CURRENT i18n.language query key — both are keyed on
 * i18n.language internally, so the instant LocaleProvider calls
 * changeLanguage, the query key changes and `data` goes back to `undefined`
 * for a beat. If the offered-set fallback for that gap were ALL_LOCALES, a
 * candidate the shop doesn't actually offer (e.g. a stale local choice)
 * would look "offered" again for that beat, flipping the resolved language
 * back — which flips the query key again, ad infinitum. Caching the last-
 * known real set means the fallback during that gap is still correct.
 */
export const SHOP_ENABLED_KEY = 'f2m_shop_enabled_locales';

const read = (key: string): Locale | null => {
  try {
    const raw = localStorage.getItem(key);
    return isLocale(raw) ? raw : null;
  } catch {
    return null; // private mode / disabled storage
  }
};

const write = (key: string, value: Locale): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode / quota — ignore */
  }
};

export const readChoice = (): Locale | null => read(CHOICE_KEY);
export const writeChoice = (value: Locale): void => write(CHOICE_KEY, value);
export const readCachedShopDefault = (): Locale | null => read(SHOP_DEFAULT_KEY);
export const writeCachedShopDefault = (value: Locale): void => write(SHOP_DEFAULT_KEY, value);

export const readCachedShopEnabled = (): Locale[] | null => {
  try {
    const raw = localStorage.getItem(SHOP_ENABLED_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const claimed = parsed.filter(isLocale);
    return claimed.length > 0 ? claimed : null;
  } catch {
    return null; // private mode / disabled storage / corrupted JSON
  }
};

export const writeCachedShopEnabled = (value: readonly Locale[]): void => {
  try {
    localStorage.setItem(SHOP_ENABLED_KEY, JSON.stringify(value));
  } catch {
    /* private mode / quota — ignore */
  }
};

export interface ResolveLocaleInput {
  /** users.locale from GET /users/me — a registered user OR a guest row. */
  userLocale?: string | null;
  /** The explicit pick stored on this device. */
  choice?: Locale | null;
  /** accounts.default_locale from GET /accounts/info (or its cache). */
  shopDefault?: Locale | null;
  /** The shop's offered set; every candidate is clamped to it. */
  enabled?: readonly Locale[];
}

/**
 * user preference → explicit local choice → shop default → en.
 * `navigator.language` is deliberately absent: a shop's default is the
 * photographer's decision and outranks browser headers.
 */
export function resolveLocale({
  userLocale,
  choice,
  shopDefault,
  enabled = ALL_LOCALES,
}: ResolveLocaleInput): Locale {
  const offered = (value: string | null | undefined): value is Locale =>
    isLocale(value) && enabled.includes(value);

  if (offered(userLocale)) return userLocale;
  if (offered(choice)) return choice;
  if (offered(shopDefault)) return shopDefault;
  return enabled.includes(FALLBACK_LOCALE) ? FALLBACK_LOCALE : (enabled[0] ?? FALLBACK_LOCALE);
}

/**
 * The language to initialise i18next with, before any network call. The live
 * values (user preference, shop default) arrive later and LocaleProvider
 * reconciles — inside the Suspense window index.tsx already renders while the
 * translation JSON loads.
 */
export const bootLocale = (): Locale =>
  readChoice() ?? readCachedShopDefault() ?? FALLBACK_LOCALE;
